import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/LanguageSelector";

const buildNavLinks = (t: (k: string) => string) => [
  { label: t("publicNav.home"), path: "/" },
  { label: t("publicNav.pricing"), path: "/tarifs" },
  {
    label: t("publicNav.solutions"),
    children: [
      { label: t("publicNav.realEstate"), path: "/solutions/immobilier" },
      { label: t("publicNav.bookstores"), path: "/solutions/librairies" },
      { label: t("publicNav.acquisition"), path: "/solutions/reprise" },
      { label: t("publicNav.financing"), path: "/solutions/financement" },
    ],
  },
  { label: t("publicNav.vision"), path: "/vision" },
  { label: t("publicNav.crowdfunding"), path: "/crowdfunding" },
  { label: t("publicNav.seminar"), path: "/seminaire" },
  { label: t("publicNav.workshops"), path: "/ateliers" },
  { label: t("publicNav.recruitment"), path: "/recrutement" },
  { label: t("publicNav.diagnostic"), path: "/diagnostic" },
  { label: t("publicNav.ideas"), path: "/bourse-idees" },
  { label: t("publicNav.contact"), path: "/contact" },
];

const buildFooterSections = (t: (k: string) => string) => [
  {
    title: t("footer.solutions"),
    links: [
      { label: t("publicNav.realEstate"), path: "/solutions/immobilier" },
      { label: t("publicNav.bookstores"), path: "/solutions/librairies" },
      { label: t("publicNav.acquisition"), path: "/solutions/reprise" },
      { label: t("publicNav.financing"), path: "/solutions/financement" },
    ],
  },
  {
    title: t("footer.services"),
    links: [
      { label: t("footer.aiDiagnostic"), path: "/diagnostic" },
      { label: t("footer.aiRecruitment"), path: "/recrutement" },
      { label: t("publicNav.workshops"), path: "/ateliers" },
      { label: t("publicNav.seminar"), path: "/seminaire" },
    ],
  },
  {
    title: t("footer.community"),
    links: [
      { label: t("publicNav.crowdfunding"), path: "/crowdfunding" },
      { label: t("publicNav.ideas"), path: "/bourse-idees" },
      { label: t("publicNav.vision"), path: "/vision" },
      { label: t("publicNav.pricing"), path: "/tarifs" },
    ],
  },
  {
    title: t("footer.access"),
    links: [
      { label: t("common.signIn"), path: "/auth" },
      { label: t("nav.dashboard"), path: "/dashboard" },
      { label: t("publicNav.contact"), path: "/contact" },
    ],
  },
];

export const PublicLayout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const navLinks = buildNavLinks(t);
  const footerSections = buildFooterSections(t);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">G</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">Grafton IA</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-6 text-sm text-muted-foreground">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label} className="relative group">
                  <button className="hover:text-foreground transition-colors flex items-center gap-1">
                    {link.label}
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <div className="bg-card border border-border rounded-lg shadow-xl p-2 min-w-[200px]">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`block px-3 py-2 rounded-md text-sm hover:bg-secondary transition-colors ${location.pathname === child.path ? "text-primary" : ""}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path!}
                  className={`hover:text-foreground transition-colors ${location.pathname === link.path ? "text-primary font-medium" : ""}`}
                >
                  {link.label}
                </Link>
              )
            )}
            {user ? (
              <div className="flex items-center gap-3">
                <LanguageSelector />
                <Link to="/dashboard">
                  <Button variant="hero" size="sm">{t("nav.dashboard")}</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={signOut}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <LanguageSelector />
                <Link to="/auth">
                  <Button variant="hero" size="sm" data-testid="signin-btn">{t("common.signIn")}</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="lg:hidden text-foreground" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl max-h-[80vh] overflow-y-auto">
            <div className="container py-4 space-y-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <button
                      onClick={() => setSolutionsOpen(!solutionsOpen)}
                      className="w-full flex justify-between items-center px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground"
                    >
                      {link.label}
                      <svg className={`w-3 h-3 transition-transform ${solutionsOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {solutionsOpen && link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => setMobileOpen(false)}
                        className="block pl-6 px-3 py-2 text-sm text-muted-foreground hover:text-primary"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path!}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-3 py-2.5 text-sm hover:text-primary ${location.pathname === link.path ? "text-primary" : "text-muted-foreground"}`}
                  >
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-3 border-t border-border/50 space-y-2">
                <div className="flex justify-center py-1">
                  <LanguageSelector />
                </div>
                {user ? (
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                    <Button variant="hero" size="sm" className="w-full">{t("nav.dashboard")}</Button>
                  </Link>
                ) : (
                  <Link to="/auth" onClick={() => setMobileOpen(false)}>
                    <Button variant="hero" size="sm" className="w-full">{t("common.signIn")}</Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Content */}
      <main className="flex-1 pt-16">{children}</main>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-card/50">
        <div className="container py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {footerSections.map((section) => (
              <div key={section.title}>
                <h4 className="font-semibold text-sm text-primary mb-4">{section.title}</h4>
                <ul className="space-y-2.5">
                  {section.links.map((link) => (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs">G</span>
              </div>
              <span className="font-semibold">Grafton IA</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 Grafton IA. {t("footer.rights")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
